import React, { useCallback, useState } from 'react';
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout';
import { H2, Label, Text } from '@amzn/stencil-react-components/text';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input, InputWrapper } from '@amzn/stencil-react-components/input';
import {
  MAX_ALLOWED_DESCRIPTION_LENGTH,
  MAX_ALLOWED_NAME_LENGTH,
} from '../enums/schedulingConstant';
import { Button } from '@amzn/stencil-react-components/button';
import ButtonWithLoader from '../components/atoms/ButtonWithLoader';
import { useHistory } from 'react-router-dom';
import { HOME_PAGE } from '../constants/routesConstant';
import isEmpty from 'lodash/isEmpty';
import { useParentExNudge } from '../hooks/useParentExNudge';
import getFileContent from '../util/segmentCreation/getFileContent';
import ParentExUploadContainer from './ParentExUploadContainer';

export const ParentExCampaignContainer = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { uploadParentExFile, isLoading } = useParentExNudge();
  const [parentExCampaign, setParentExCampaign] = useState();
  const [error, setError] = useState();
  const [parentExDescription, setParentExDescription] = useState();
  const [fileContent, setFileContent] = useState({
    isUploaded: false,
    fileName: '',
    content: '',
  });

  const [file, setFile] = useState({});

  const onParentExCampaignChange = e => {
    setParentExCampaign(e?.target.value);
  };

  const onDescriptionChange = e => {
    setParentExDescription(e?.target.value);
  };

  const getFileDetails = async () => {
    setError(false);
    const input = document.querySelector('#parent_ex_file')['files'][0];
    getFileContent(input).then(data => {
      if (isEmpty(data)) {
        setError(true);
      }
      setFile(input);
      setFileContent({
        isUploaded: true,
        fileName: input?.name,
        content: data,
      });
    });
  };

  const onFileSelectClick = async () => {
    const element = document.getElementById('parent_ex_file');
    element.click();
    await getFileDetails();
  };

  const onSubmit = useCallback(
    () => {
      uploadParentExFile(file);
    },
    [file],
  );

  const removeParentExFile = () => {
    setFile({});
    setFileContent({
      isUploaded: false,
      fileName: '',
      content: '',
    });
    setError(false);
  };
  return (
    <React.Fragment>
      <Col width={'98%'} flex="1" gridGap={'S300'} data-testid="create-segment">
        <H2>
          <FormattedMessage
            id="app.parentEx.campaignLabel"
            defaultMessage="Create ParentEx Nudge"
          />
        </H2>
        <Row display="flex" flexDirection="column" gridGap={20}>
          {/* ParentEx Nudge name */}

          <View width={'60%'} height={64}>
            <InputWrapper
              labelText=""
              id="parentex-name-wrapper"
              footer={formatMessage({ id: 'app.parentEx.parentexName.info' })}
            >
              {inputProps => (
                <>
                  <Label
                    fontSize={'T300'}
                    display="flex"
                    htmlFor="rule-filter-1"
                    id="rule-filter-label1"
                  >
                    <Text
                      style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                        gap: '5px',
                      }}
                    >
                      <FormattedMessage
                        id="app.parentEx.campaignName.label"
                        defaultMessage="ParentEx Campaign Name"
                      />
                      <Text
                        alignItems="center"
                        justifyContent="center"
                        fontSize="T100"
                        fontWeight="light"
                        textAlign="left"
                      >
                        <FormattedMessage id="app.parentEx.optional.label" />
                      </Text>
                    </Text>
                  </Label>
                  <Input
                    {...inputProps}
                    data-testid="parentEx-name-input"
                    value={parentExCampaign}
                    maxLength={MAX_ALLOWED_NAME_LENGTH}
                    onChange={e => onParentExCampaignChange(e)}
                  />
                </>
              )}
            </InputWrapper>
          </View>

          <Spacer height={32} />

          {/* ParentEx description */}

          <View width={'60%'} height={64}>
            <InputWrapper id="parentex-description-wrapper" labelText="">
              {inputProps => (
                <>
                  <Label
                    fontSize={'T300'}
                    display="flex"
                    justifyContent="center"
                    gridGap="10"
                    htmlFor="rule-filter-1"
                    id="rule-filter-label1"
                  >
                    <Text
                      style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                        gap: '5px',
                      }}
                    >
                      <FormattedMessage
                        id="app.parentEx.campaignDescription.label"
                        defaultMessage="Description"
                      />
                      <Text
                        alignItems="center"
                        justifyContent="center"
                        fontSize="T100"
                        fontWeight="light"
                        textAlign="left"
                      >
                        <FormattedMessage id="app.parentEx.optional.label" />
                      </Text>
                    </Text>
                  </Label>
                  <Input
                    {...inputProps}
                    value={parentExDescription}
                    maxLength={MAX_ALLOWED_DESCRIPTION_LENGTH}
                    data-testid="parentex-description-input"
                    onChange={e => onDescriptionChange(e)}
                  />
                </>
              )}
            </InputWrapper>
          </View>

          {/* ParentEx file upload */}
          <Col gridGap="S200">
            <ParentExUploadContainer
              onClick={onFileSelectClick}
              onFileRemove={removeParentExFile}
              fileContent={fileContent}
              onFileChange={getFileDetails}
            />
          </Col>

          {error && isEmpty(fileContent.content) ? (
            <Text data-testid="error-container" className="errorText">
              <FormattedMessage
                id="app.createSegment.import.fileUploadErrorMessage"
                defaultMessage="File is empty/invalid. Please upload a valid csv file."
              />
            </Text>
          ) : null}

          <Spacer height={32} />

          {/* Footer */}
          <Row gridGap={'m200'}>
            <ButtonWithLoader
              data-testid="parentex-save-btn"
              isLoading={isLoading}
              isDisabled={isEmpty(fileContent.content) || error}
              isPrimary={true}
              onClickHandler={onSubmit}
            >
              <FormattedMessage
                id="app.parentEx.createParentEx.button"
                defaultMessage="Create ParentEx Nudge"
              />
            </ButtonWithLoader>
            <Spacer width={50} />
            <Button
              data-testid="cancel-button"
              onClick={() => history.push(HOME_PAGE)}
              tertiary={true}
            >
              <FormattedMessage id="app.createSegment.button.cancel" defaultMessage="Cancel" />
            </Button>
          </Row>
          <Spacer width="20px" />
        </Row>
      </Col>
    </React.Fragment>
  );
};

export default ParentExCampaignContainer;
